import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  TroubleshootingStep,
  optionStart,
  optionResolved,
  optionEnd,
  optionNext,
} from '../interface/troubleshoot-es2000.interface';
import { defaultLanguage } from '../../shared/constants/language.constants';
import { LandingService } from '../../landing-page/service/landing.service';

@Injectable({
  providedIn: 'root',
})
export class TroubleShootEs2000Service {
  stepsList: any;
  issueName: string | undefined = '';
  currentLanguageName: string = defaultLanguage;

  constructor(
    private http: HttpClient,
    private landingService: LandingService,
  ) {
    try {
      const stored = localStorage.getItem('deviceData');
      const selected = stored ? JSON.parse(stored).languageSelected : null;
      this.currentLanguageName = selected || defaultLanguage;
    } catch {
      this.currentLanguageName = defaultLanguage;
    }

    this.landingService.languageName$.subscribe((langName) => {
      this.currentLanguageName = langName || defaultLanguage;
    });
  }

  getTroubleShootEs2000Issues(): any[] {
    let issues: any[] = [];
    for (const option of this.es2000.options) {
      //Abscent yes/no step
      if (option.steps && option.steps.length > 1) {
        issues.push({ key: option.steps[0].key, label: option.label });
      } else {
        //present yes/no step
        issues.push({ key: option.key, label: option.label });
      }
    }
    return issues;
  }

  setIssuename(issuename: string | undefined) {
    this.issueName = issuename;
  }

  getIssuename() {
    return this.issueName;
  }

  getTroubleShootStep(searchKey: string): TroubleshootingStep | null {
    return this.findObjectByKey(searchKey, this.es2000.options);
  }

  findObjectByKey(keyToFind: string, options: any[]): any | null {
    this.stepsList = [];
    for (const option of options) {
      if (option.key === keyToFind) {
        option.steps
          ? (this.stepsList = option.steps)
          : (this.stepsList = undefined);
        return option;
      }

      if (option.steps) {
        for (const step of option.steps) {
          if (step.key === keyToFind) {
            this.stepsList = option.steps;
            return step;
          }
          const foundObject = this.findObjectByKey(keyToFind, step.options);
          if (foundObject) {
            return foundObject;
          }
        }
      }
    }
    return null;
  }

  getStepState() {
    return this.stepsList;
  }

  getStepIndex(key: string) {
    for (let i = 0; i < this.stepsList.length; i++) {
      if (this.stepsList[i].key === key) {
        return i;
      }
    }
    return -1; // If the key is not found
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   *
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  /** Log a HeroService message with the MessageService */
  private log(message: string) {}

  es2000: TroubleshootingStep = {
    key: 'es-2000',
    options: [
      {
        key: 'es-2000-machine-is-down',
        label: [
          'Machine is down',
          'Máquina no operativa',
          'Machine hors service',
          'Machine is buiten bedrijf',
          'Macchina fuori servizio',
          '機械が停止している',
          'Máquina fora de serviço',
          'Maschine außer Betrieb',
        ],
        option: 'troubleshooting.es2000.machineDown',
        steps: [
          {
            key: 'es-2000-filling-with-water',
            question: 'troubleshooting.es2000.machineDownFilling',
            label: [
              'Is the machine filling with water?',
              '¿La máquina esta llenandose con agua?',
              'La machine se remplit-elle d’eau ?',
              'Wordt de machine gevuld met water?',
              'La macchina si sta riempiendo di acqua?',
              '機械に給水されていますか？',
              'A máquina está enchendo com água?',
              'Füllt sich die Maschine mit Wasser?',
            ],
            optionsLabel: '',
            options: [
              {
                key: 'es-2000-yes',
                option: 'troubleshooting.yes',
                label: ['Yes', 'Si', 'Oui', 'Ja', 'Sì', 'はい', 'Sim', 'Ja'],
                icon: 'check',
                steps: [
                  {
                    key: 'es-2000-machine-down-filling-intro',
                    showIntro: true,
                    options: [optionStart],
                  },
                  {
                    key: 'es-2000-check-on-switch',
                    question: 'troubleshooting.es2000.machineDownOnOff',
                    label: [
                      'Check to make sure the ON/OFF switch is set to the ON position.',
                      'Verifique que el interruptor de ON/OFF esta en la posición ON',
                      'Vérifiez que l’interrupteur marche/arrêt est en position Marche',
                      'Controleer of de AAN/UIT-schakelaar op AAN staat',
                      'Verificare che l’interruttore ON/OFF sia in posizione ON',
                      'ON/OFF スイッチが「ON」になっているか確認してください',
                      'Verifique se o interruptor LIGA/DESLIGA está na posição LIGA.',
                      'Prüfen Sie, ob der EIN/AUS-Schalter auf EIN steht.',
                    ],
                    images: [
                      {
                        url: 'assets/images/troubleshooting/es-2000/on-off-switch.jpg',
                        alt: '',
                      },
                    ],
                    optionsLabel: '',
                    options: [optionResolved, optionEnd],
                  },
                ],
              },
              {
                key: 'es-2000-not-sure',
                option: 'troubleshooting.notSure',
                label: [
                  'Not Sure',
                  'No estoy seguro(a)',
                  'Je ne suis pas sûr(e)',
                  'Niet zeker',
                  'Non sono sicuro(a)',
                  'よくわからない',
                  'Não tenho certeza',
                  'Nicht sicher',
                ],
                icon: 'question',
              },
              {
                key: 'es-2000-no',
                option: 'troubleshooting.no',
                label: [
                  'No',
                  'No',
                  'Non',
                  'Nee',
                  'No',
                  'いいえ',
                  'Não',
                  'Nein',
                ],
                icon: 'cross',
                steps: [
                  {
                    key: 'es-2000-machine-down-not-filling-intro',
                    showIntro: true,
                    options: [optionStart],
                  },
                  {
                    key: 'es-2000-check-float-switch',
                    question: 'troubleshooting.es2000.machineDownFloatSwitch',
                    label: [
                      'Check to make sure the float switch is not stuck.',
                      'Verifique que el interruptor de flotador no se encuentra atascado',
                      'Vérifiez que le flotteur n’est pas bloqué',
                      'Controleer of de vlotterschakelaar niet vastzit',
                      'Verificare che il galleggiante non sia bloccato',
                      'フロートスイッチが固着していないか確認してください',
                      'Verifique se a boia (float switch) não está presa.',
                      'Prüfen Sie, dass der Schwimmerschalter nicht klemmt.',
                    ],
                    images: [
                      {
                        url: 'assets/images/troubleshooting/es-2000/float-valve-down.jpg',
                        alt: '',
                      },
                    ],
                    optionsLabel: '',
                    options: [optionResolved, optionNext],
                  },
                  {
                    key: 'es-2000-check-water-on',
                    question: 'troubleshooting.es2000.machineDownWater',
                    label: [
                      'Check to make sure the water is on.',
                      'Verifique que el agua esta corriendo',
                      'Vérifiez que l’eau est en marche',
                      'Controleer of het water is ingeschakeld',
                      "Verificare che l'acqua sia aperta",
                      '給水がオンになっているか確認してください',
                      'Verifique se a água está ligada.',
                      'Prüfen Sie, ob die Wasserzufuhr eingeschaltet ist.',
                    ],
                    optionsLabel: '',
                    options: [optionResolved, optionNext],
                  },
                  {
                    key: 'es-2000-check-drain-not-shut',
                    question: 'troubleshooting.es2000.machineDownDrain',
                    label: [
                      'Check to make sure the drain is not shut.',
                      'Verifique el desagüe no esté cerrado',
                      'Vérifiez que le drain n’est pas fermé',
                      'Controleer of de afvoer niet gesloten is',
                      'Verificare che lo scarico non sia chiuso',
                      '排水が閉まっていないか確認してください',
                      'Verifique se o dreno não está fechado.',
                      'Prüfen Sie, dass der Abfluss nicht geschlossen ist.',
                    ],
                    images: [
                      {
                        url: 'assets/images/troubleshooting/es-2000/drain-is-shut.jpg',
                        alt: '',
                      },
                    ],
                    optionsLabel: '',
                    options: [optionResolved, optionEnd],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        key: 'es-2000-machine-is-leaking',
        option: 'troubleshooting.es2000.leaking',
        label: [
          'Machine is leaking',
          'La máquina tiene una fuga de agua',
          'La machine fuit',
          'Machine lekt',
          'La macchina perde',
          '機械から漏れがある',
          'A máquina está vazando',
          'Die Maschine hat ein Leck',
        ],
        steps: [
          {
            key: 'es-2000-leak-from-front',
            question: 'troubleshooting.es2000.leakingFront',
            label: [
              'Is the leak coming from the front of the machine?',
              '¿La fuga se encuentra en el frente de la máquina?',
              'La fuite vient-elle de l’avant de la machine ?',
              'Komt de lekkage van de voorkant van de machine?',
              'La perdita proviene dalla parte anteriore della macchina?',
              '漏れは機械の前面から発生していますか？',
              'O vazamento vem da parte frontal da máquina?',
              'Kommt das Leck von der Vorderseite der Maschine?',
            ],
            optionsLabel: '',
            options: [
              {
                key: 'es-2000-yes',
                option: 'troubleshooting.yes',
                label: ['Yes', 'Si', 'Oui', 'Ja', 'Sì', 'はい', 'Sim', 'Ja'],
                icon: 'check',
                resolve: false,
                steps: [
                  {
                    key: 'es-2000-leak-front-intro',
                    showIntro: true,
                    options: [optionStart],
                  },
                  {
                    key: 'es-2000-leak-end-caps',
                    question: 'troubleshooting.es2000.leakingFrontEndCaps',
                    label: [
                      'Check to make sure the end caps are in place. They may have dropped into the scrap screen or not been replaced after cleaning.',
                      'Verifique que las tapas de las puntas estan en su puesto. Estas se habrían podido caer en la pantalla de deshechos o no se replazaron despúes de una limpieza',
                      'Vérifiez que les bouchons de fin sont en place. Ils ont peut-être tombé dans l’écran de rebut ou n’ont pas été remplacés après le nettoyage.',
                      'Controleer of de eindkappen op hun plaats zitten. Ze kunnen in het zeefscherm zijn gevallen of na het reinigen niet teruggeplaatst zijn.',
                      'Verificare che i tappi terminali siano in posizione. Potrebbero essere caduti nella griglia dei residui o non essere stati rimessi dopo la pulizia.',
                      'エンドキャップが所定の位置にあるか確認してください。清掃後に戻されていない、またはくず受けに落ちている可能性があります。',
                      'Verifique se as tampas de extremidade estão no lugar. Elas podem ter caído na tela de resíduos ou não ter sido recolocadas após a limpeza.',
                      'Prüfen Sie, ob die Endkappen an ihrem Platz sind. Sie könnten in das Sieb gefallen sein oder nach der Reinigung nicht wieder eingesetzt worden sein.',
                    ],
                    images: [
                      {
                        url: 'assets/images/troubleshooting/es-2000/end-cap-off.jpg',
                        alt: '',
                      },
                      {
                        url: 'assets/images/troubleshooting/es-2000/wash-arm-with-end-cap.jpg',
                        alt: '',
                      },
                      {
                        url: 'assets/images/troubleshooting/es-2000/end-cap-off.jpg',
                        alt: '',
                      },
                    ],
                    optionsLabel: '',
                    options: [optionResolved, optionNext],
                  },
                  {
                    key: 'es-2000-leak-front-drain-steam',
                    question: 'troubleshooting.es2000.leakingFrontDrain',
                    label: [
                      'Check the drain stem for objects.',
                      'Verifique la varilla del drenaje por objetos',
                      'Vérifiez la tige de vidange pour des objets',
                      'Controleer de afvoerstift op voorwerpen',
                      'Verificare la spina/asta dello scarico per oggetti',
                      '排水ステムに異物がないか確認してください',
                      'Verifique se há objetos na haste do dreno.',
                      'Prüfen Sie die Ablaufstange auf Fremdkörper.',
                    ],
                    images: [
                      {
                        url: 'assets/images/troubleshooting/es-2000/drain-stem.jpg',
                        alt: '',
                      },
                    ],
                    optionsLabel: '',
                    options: [optionResolved, optionNext],
                  },
                  {
                    key: 'es-2000-leak-front-float-switch',
                    question: 'troubleshooting.es2000.leakingFrontFloat',
                    label: [
                      'Check to make sure the float switch is not stuck in the down position.',
                      'Verifique que el interruptor de flotador no se encuentra atascado en la posición de abajo',
                      'Vérifiez que le flotteur n’est pas coincé en position basse',
                      'Controleer of de vlotterschakelaar niet vastzit in de lage stand',
                      'Verificare che il galleggiante non sia bloccato in posizione bassa',
                      'フロートスイッチが下がったまま固着していないか確認してください',
                      'Verifique se a boia não está presa na posição abaixada.',
                      'Prüfen Sie, dass der Schwimmerschalter nicht in der unteren Position festhängt.',
                    ],
                    images: [
                      {
                        url: 'assets/images/troubleshooting/es-2000/float-valve-down.jpg',
                        alt: '',
                      },
                    ],
                    optionsLabel: '',
                    options: [optionResolved, optionEnd],
                  },
                ],
              },
              {
                key: 'es-2000-not-sure',
                option: 'troubleshooting.notSure',
                label: [
                  'Not Sure',
                  'No estoy seguro(a)',
                  'Je ne suis pas sûr(e)',
                  'Niet zeker',
                  'Non sono sicuro(a)',
                  'よくわからない',
                  'Não tenho certeza',
                  'Nicht sicher',
                ],
                icon: 'question',
                resolve: false,
                steps: [],
              },
              {
                key: 'es-2000-no',
                option: 'troubleshooting.no',
                label: [
                  'No',
                  'No',
                  'Non',
                  'Nee',
                  'No',
                  'いいえ',
                  'Não',
                  'Nein',
                ],
                icon: 'cross',
                resolve: false,
                steps: [
                  {
                    key: 'es-2000-leak-intro',
                    showIntro: true,
                    options: [optionStart],
                  },
                  {
                    key: 'es-2000-leak-drain-stem',
                    question: 'troubleshooting.es2000.leakingSideDrain',
                    label: [
                      'Check the drain stem for objects.',
                      'Verifique la varilla del drenaje por objetos',
                      'Vérifiez la tige de vidange pour des objets',
                      'Controleer de afvoerstift op voorwerpen',
                      'Verificare la spina/asta dello scarico per oggetti',
                      '排水ステムに異物がないか確認してください',
                      'Verifique se há objetos na haste do dreno.',
                      'Prüfen Sie die Ablaufstange auf Fremdkörper.',
                    ],
                    images: [
                      {
                        url: 'assets/images/troubleshooting/es-2000/drain-stem.jpg',
                        alt: '',
                      },
                    ],
                    optionsLabel: '',
                    options: [optionResolved, optionNext],
                  },
                  {
                    key: 'es-2000-leak-float-switch',
                    question: 'troubleshooting.es2000.leakingSideFloat',
                    label: [
                      'Check to make sure the float switch is not stuck in the down position.',
                      'Verifique que el interruptor de flotador no se encuentra atascado en la posición de abajo',
                      'Vérifiez que le flotteur n’est pas coincé en position basse',
                      'Controleer of de vlotterschakelaar niet vastzit in de lage stand',
                      'Verificare che il galleggiante non sia bloccato in posizione bassa',
                      'フロートスイッチが下がったまま固着していないか確認してください',
                      'Verifique se a boia não está presa na posição abaixada.',
                      'Stellen Sie sicher, dass der Schwimmerschalter nicht in der unteren Position festsitzt.',
                    ],
                    images: [
                      {
                        url: 'assets/images/troubleshooting/es-2000/float-valve-down.jpg',
                        alt: '',
                      },
                    ],
                    optionsLabel: '',
                    options: [optionResolved, optionEnd],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        key: 'es-2000-machine-not-cleaning',
        option: 'troubleshooting.es2000.cleaning',
        label: [
          'Machine is not cleaning properly',
          'La máquina no esta limpiando propiamente',
          'La machine ne nettoie pas correctement',
          'De machine reinigt niet goed',
          'La macchina non pulisce correttamente',
          '機械が正しく洗浄できていない',
          'A máquina não está limpando corretamente',
          'Die Maschine reinigt nicht richtig',
        ],
        steps: [
          {
            key: 'es-2000-not-cleaning-intro',
            showIntro: true,
            options: [optionStart],
          },
          {
            key: 'es-2000-not-cleaning-product-in-machine',
            question: 'troubleshooting.es2000.cleaningProduct',
            label: [
              'Check to make sure there is product in the machine.',
              'Verifique que hay prodúcto en la máquina',
              'Vérifiez qu’il y a du produit dans la machine',
              'Controleer of er product in de machine zit',
              'Verificare che ci sia prodotto nella macchina',
              '機械に薬剤（製品）が入っているか確認してください',
              'Verifique se há produto na máquina.',
              'Stellen Sie sicher, dass sich Produkt in der Maschine befindet.',
            ],
            images: [
              {
                url: 'assets/images/troubleshooting/es-2000/chemistry.jpg',
                alt: '',
                video:
                  'https://www.youtube.com/embed/F4M5ktHewrI?si=S7bn_8n3On5zvxaO&amp;amp;start=46&amp;amp;end=55',
              },
            ],
            optionsLabel: '',
            options: [optionResolved, optionNext],
          },
          {
            key: 'es-2000-not-cleaning-probe-bottom',
            question: 'troubleshooting.es2000.cleaningProbe',
            label: [
              'Verify that the product probe is at the bottom of the jug.',
              'Verifique que la sonda del prodúcto esta en el fondo del contenedor',
              'Vérifiez que la sonde du produit est au fond de la carafe',
              'Controleer of de productsonde op de bodem van de kan zit',
              'Verificare che la sonda del prodotto sia sul fondo del contenitore',
              '製品プローブが容器の底に届いているか確認してください',
              'Verifique se a sonda do produto está no fundo do recipiente.',
              'Stellen Sie sicher, dass die Produktsonde am Boden des Kanisters sitzt.',
            ],
            optionsLabel: '',
            images: [
              {
                url: 'assets/images/troubleshooting/es-2000/chemistry.jpg',
                alt: '',
                video:
                  'https://www.youtube.com/embed/F4M5ktHewrI?si=S7bn_8n3On5zvxaO&amp;amp;start=46&amp;amp;end=55',
              },
            ],
            options: [optionResolved, optionNext],
          },
          {
            key: 'es-2000-not-cleaning-color-of-line',
            question: 'troubleshooting.es2000.cleaningLineColor',
            label: [
              'Verify that the color of the line going into the product matches the product color.',
              'Verifique que el color de la linea que entra al prodúcto es del mismo color del prodúcto',
              'Vérifiez que la couleur de la ligne allant dans le produit correspond à la couleur du produit',
              'Controleer of de kleur van de slang die het product in gaat overeenkomt met de productkleur',
              'Verificare che il colore della linea in ingresso corrisponda al colore del prodotto',
              '製品に接続されたチューブの色が製品の色と一致しているか確認してください',
              'Verifique se a cor da linha que entra no produto corresponde à cor do produto.',
              'Prüfen Sie, ob die Schlauchfarbe mit der Produktfarbe übereinstimmt.',
            ],
            optionsLabel: '',
            images: [
              {
                url: 'assets/images/troubleshooting/es-2000/chemistry.jpg',
                alt: '',
                video:
                  'https://www.youtube.com/embed/F4M5ktHewrI?si=S7bn_8n3On5zvxaO&amp;amp;start=46&amp;amp;end=55',
              },
            ],
            options: [optionResolved, optionNext],
          },
          {
            key: 'es-2000-not-cleaning-prime-button',
            question: 'troubleshooting.es2000.cleaningPrime',
            label: [
              'Turn machine on. Press prime buttons for sanitizer, detergent, and rinse to verify product is pulling.',
              'Encienda la máquina. Presione los botones del sanitizante, detergente y rinse para verificar que el producto fluye',
              'Allumez la machine. Appuyez sur les boutons de prélavage pour vérifier que le produit est aspiré',
              'Zet de machine aan. Druk op de prime-knoppen voor desinfectiemiddel, wasmiddel en naspoelmiddel om te controleren of er product wordt aangezogen',
              'Accendere la macchina. Premere i pulsanti di adescamento per disinfettante, detergente e risciacquo per verificare l’aspirazione del prodotto',
              '機械の電源を入れ、除菌剤・洗剤・リンスのプライムボタンを押して、薬剤が吸い上げられるか確認してください',
              'Ligue a máquina. Pressione os botões de priming de desinfetante, detergente e enxágue para verificar se o produto está sendo puxado.',
              'Maschine einschalten. Drücken Sie die Priming-Tasten für Desinfektionsmittel, Reiniger und Klarspüler, um den Produktfluss zu prüfen.',
            ],
            images: [
              {
                url: 'assets/images/troubleshooting/es-2000/prime-button.jpg',
                alt: '',
              },
            ],
            optionsLabel: '',
            options: [optionResolved, optionNext],
          },
          {
            key: 'es-2000-not-cleaning-drain-closed',
            question: 'troubleshooting.es2000.cleaningTank',
            label: [
              'Check to make sure there is water in the tank. Ensure the drain is closed (EC models) or shutting properly (ES models).',
              'Verifique que hay agua en el tanque. Asegurese que el desagüe esta cerrado (modelos EC) o cerrando apropiadamente (modelos ES)',
              'Vérifiez qu’il y a de l’eau dans le réservoir. Assurez-vous que la vidange est fermée (modèles EC) ou se ferme correctement (modèles ES)',
              'Controleer of er water in de tank staat. Zorg dat de afvoer dicht is (EC-modellen) of correct sluit (ES-modellen)',
              'Verificare che ci sia acqua nel serbatoio. Assicurarsi che lo scarico sia chiuso (modelli EC) o che si chiuda correttamente (modelli ES)',
              'タンク内に水があるか確認し、排水が閉じている（ECモデル）または正しく閉まる（ESモデル）ことを確認してください',
              'Verifique se há água no tanque. Certifique-se de que o dreno esteja fechado (modelos EC) ou fechando corretamente (modelos ES).',
              'Prüfen Sie, ob sich Wasser im Tank befindet. Stellen Sie sicher, dass der Abfluss geschlossen ist (EC-Modelle) bzw. korrekt schließt (ES-Modelle).',
            ],
            optionsLabel: '',
            options: [optionResolved, optionNext],
          },
          {
            key: 'es-2000-not-cleaning-pickup-probe',
            question: 'troubleshooting.es2000.cleaningPickup',
            label: [
              'Ensure the correct product is in the pickup probe.',
              'Asegurese que el prodúcto correcto esta en la sonda',
              'Assurez-vous que le bon produit est dans la sonde de prélèvement',
              'Zorg dat het juiste product op de aanzuigsonde is aangesloten',
              'Assicurarsi che il prodotto corretto sia collegato alla sonda di aspirazione',
              '正しい製品が吸上げプローブに接続されていることを確認してください',
              'Certifique-se de que o produto correto está conectado à sonda de sucção.',
              'Stellen Sie sicher, dass das richtige Produkt an der Ansaugsonde angeschlossen ist.',
            ],
            optionsLabel: '',
            options: [optionResolved, optionNext],
          },
          {
            key: 'es-2000-not-cleaning-no-air',
            question: 'troubleshooting.es2000.cleaningPrimeProduct',
            label: [
              'Prime the product to ensure air is not being pulled in.',
              'Prepare el prodúcto para asegurar que no hay aire en la linea',
              'Préchauffez le produit pour s’assurer qu’aucun air n’est aspiré dans la ligne',
              'Prime het product om zeker te zijn dat er geen lucht wordt aangezogen',
              'Adescare il prodotto per assicurarsi che non entri aria nella linea',
              'エアが混入しないよう、製品をプライミングしてください',
              'Faça o priming do produto para garantir que não esteja entrando ar na linha.',
              'Priming durchführen, um sicherzustellen, dass keine Luft angesaugt wird.',
            ],
            optionsLabel: '',
            options: [optionResolved, optionEnd],
          },
        ],
      },
      {
        key: 'es-2000-cycle-not-starting',
        option: 'troubleshooting.es2000.cycle',
        label: [
          'Cycle not starting',
          'El ciclo no está comenzando',
          'Le cycle ne démarre pas',
          'Cyclus start niet',
          'Il ciclo non si avvia',
          'サイクルが開始しない',
          'O ciclo não inicia',
          'Zyklus startet nicht',
        ],
        steps: [
          {
            key: 'es-2000-cycle-intro',
            showIntro: true,
            options: [optionStart],
          },
          {
            key: 'es-2000-cycle-delime',
            question: 'troubleshooting.es2000.cycleDelime',
            label: [
              'Check to make sure the delime switch is turned off.',
              'Asegurese que el interruptor de descalzificación esta esta apagado',
              'Vérifiez que le commutateur de détartrage est éteint',
              'Controleer of de ontkalkschakelaar is uitgeschakeld',
              'Verificare che l’interruttore di decalcificazione sia spento',
              'デライムスイッチがオフになっているか確認してください',
              'Verifique se o interruptor de descalcificação (delime) está desligado.',
              'Stellen Sie sicher, dass der Entkalkungsschalter (Delime) ausgeschaltet ist.',
            ],
            optionsLabel: '',
            images: [
              {
                url: 'assets/images/troubleshooting/es-2000/delime-switch-normal.jpg',
                alt: '',
              },
            ],
            options: [optionResolved, optionEnd],
          },
        ],
      },
      {
        key: 'es-2000-temperature',
        option: 'troubleshooting.es2000.temperature',
        label: [
          'Issue with temperature',
          'Problema con temperatura',
          'Problème de température',
          'Temperatuurprobleem',
          'Problema di temperatura',
          '温度の問題',
          'Problema de temperatura',
          'Temperaturproblem',
        ],
        steps: [
          {
            key: 'es-2000-temp-intro',
            showIntro: true,
            options: [optionStart],
          },
          {
            key: 'es-2000-temp-incoming-water-temp',
            question: 'troubleshooting.es2000.temperatureCheck',
            label: [
              'Check incoming water to verify temperature is between 110 - 130 degrees Celsius.',
              'Verifique que el agua de entrada esta entre 110 - 130 grados centigrados',
              'Vérifiez que l’eau entrant dans la machine est entre 110 et 130 degrés Celsius',
              'Controleer of de temperatuur van het binnenkomende water tussen 110 en 130 graden Celsius ligt',
              'Controllare che l’acqua in ingresso sia tra 110 e 130 gradi Celsius',
              '給水温度が110〜130℃の範囲であることを確認してください',
              'Verifique a água de entrada para confirmar que a temperatura está entre 110 e 130 graus Celsius.',
              'Prüfen Sie, ob die Zulauftemperatur zwischen 110 und 130 °C liegt.',
            ],
            images: [
              {
                url: 'assets/images/troubleshooting/es-2000/water-temp.jpg',
                alt: '',
              },
              {
                url: 'assets/images/troubleshooting/es-2000/temp.jpg',
                alt: '',
                video:
                  'https://www.youtube.com/embed/F4M5ktHewrI?si=S7bn_8n3On5zvxaO&amp;amp;start=57&amp;amp;end=83',
              },
            ],
            optionsLabel: '',
            options: [optionResolved, optionEnd],
          },
        ],
      },
      {
        key: 'es-2000-runs-constantly',
        option: 'troubleshooting.es2000.runs',
        label: [
          'Machine runs constantly',
          'Máquina funciona constantemente',
          'La machine fonctionne en continu',
          'Machine blijft continu draaien',
          'La macchina funziona continuamente',
          '機械が連続して動作している',
          'A máquina funciona continuamente',
          'Die Maschine läuft ständig',
        ],
        steps: [
          {
            key: 'es-2000-running-intro',
            showIntro: true,
            options: [optionStart],
          },
          {
            key: 'es-2000-running-delime-switch',
            question: 'troubleshooting.es2000.runsDelime',
            label: [
              'Check to make sure the delime switch is turned off.',
              'Asegurese que el interruptor de descalzificación esta esta apagado',
              'Vérifiez que le commutateur de détartrage est éteint',
              'Controleer of de ontkalkschakelaar is uitgeschakeld',
              'Verificare che l’interruttore di decalcificazione sia spento',
              'デライムスイッチがオフになっているか確認してください',
              'Verifique se o interruptor de descalcificação (delime) está desligado.',
              'Stellen Sie sicher, dass der Entkalkungsschalter (Delime) ausgeschaltet ist.',
            ],
            optionsLabel: '',
            options: [optionResolved, optionEnd],
          },
        ],
      },
      {
        key: 'es-2000-door-wont-close',
        option: 'troubleshooting.es2000.door',
        label: [
          'Door won’t close',
          'La puerta no cierra',
          'La porte ne se ferme pas',
          'Deur gaat niet dicht',
          'La porta non si chiude',
          'ドアが閉まらない',
          'A porta não fecha',
          'Tür lässt sich nicht schließen',
        ],
        steps: [
          {
            key: 'es-2000-door-intro',
            showIntro: true,
            options: [optionStart],
          },
          {
            key: 'es-2000-door-object',
            question: 'troubleshooting.es2000.doorObject',
            label: [
              'Check to ensure an object is not stopping the door from closing.',
              'Verifique un objeto no esta impidiendo la puerta para cerrar',
              'Vérifiez qu’aucun objet n’empêche la porte de se fermer',
              'Controleer of geen enkel object voorkomt dat de deur sluit',
              'Verificare che nessun oggetto impedisca la chiusura della porta',
              '物が挟まってドアの閉鎖を妨げていないか確認してください',
              'Verifique se nenhum objeto está impedindo a porta de fechar.',
              'Stellen Sie sicher, dass kein Gegenstand das Schließen der Tür verhindert.',
            ],
            optionsLabel: '',
            options: [optionResolved, optionNext],
          },
          {
            key: 'es-2000-door-springs',
            question: 'troubleshooting.es2000.doorSprings',
            label: [
              'Check the back of the machine to ensure the springs are functional.',
              'Verifique la parte trasera de la máquina para asegurar que los resortes estan funcionales',
              'Vérifiez l’arrière de la machine pour s’assurer que les ressorts fonctionnent',
              'Controleer aan de achterkant van de machine of de veren functioneren',
              'Controllare la parte posteriore della macchina per assicurarsi che le molle siano funzionanti',
              '機械背面のバネが正常に機能しているか確認してください',
              'Verifique a parte traseira da máquina para garantir que as molas estejam funcionais.',
              'Prüfen Sie auf der Rückseite der Maschine, ob die Federn funktionieren.',
            ],
            images: [
              {
                url: 'assets/images/troubleshooting/es-2000/door-springs.jpg',
                alt: '',
              },
            ],
            optionsLabel: '',
            options: [optionResolved, optionNext],
          },
          {
            key: 'es-2000-door-handles',
            question: 'troubleshooting.es2000.doorHandles',
            label: [
              'Check to see if the handles on the outside of the machine are loose.',
              'Verifique si las manijas en la parte exterior de la máquina esten sueltas',
              'Vérifiez si les poignées à l’extérieur de la machine sont lâches',
              'Controleer of de handgrepen aan de buitenzijde van de machine los zitten',
              'Verificare se le maniglie all’esterno della macchina sono allentate',
              '機械外側のハンドルが緩んでいないか確認してください',
              'Verifique se as alças do lado externo da máquina estão soltas.',
              'Prüfen Sie, ob die Griffe an der Außenseite der Maschine locker sind.',
            ],
            optionsLabel: '',
            options: [optionResolved, optionEnd],
          },
        ],
      },
      {
        key: 'es-2000-not-draining',
        option: 'troubleshooting.es2000.drain',
        label: [
          'Machine is not draining',
          'Máquina no esta drenando',
          'La machine ne se vide pas',
          'Machine loopt niet leeg',
          'La macchina non scarica',
          '機械が排水しない',
          'A máquina não está drenando',
          'Die Maschine pumpt nicht ab',
        ],
        steps: [
          {
            key: 'es-2000-draining-intro',
            showIntro: true,
            options: [optionStart],
          },
          {
            key: 'es-2000-not-draining-object',
            question: 'troubleshooting.es2000.drainObjects',
            label: [
              'Check the drain for objects that could be plugging it.',
              'Verifique el drenaje por objetos que puedan estar taponandolo',
              'Vérifiez le drain pour détecter des objets pouvant le boucher',
              'Controleer de afvoer op voorwerpen die deze mogelijk verstoppen',
              'Controllare lo scarico per oggetti che potrebbero ostruirlo',
              '排水口を塞ぐ異物がないか確認してください',
              'Verifique o dreno por objetos que possam estar obstruindo-o.',
              'Prüfen Sie den Abfluss auf Gegenstände, die ihn verstopfen könnten.',
            ],
            images: [
              {
                url: 'assets/images/troubleshooting/es-2000/drain-objects.jpg',
                alt: '',
              },
              {
                url: 'assets/images/troubleshooting/es-2000/drain.jpg',
                alt: '',
                video:
                  'https://www.youtube.com/embed/F4M5ktHewrI?si=S7bn_8n3On5zvxaO&amp;amp;start=83&amp;amp;autoplay=1',
              },
            ],
            optionsLabel: '',
            options: [optionResolved, optionNext],
          },
          {
            key: 'es-2000-not-draining-valve',
            question: 'troubleshooting.es2000.drainValve',
            label: [
              'Run a cycle. Check to see if the outside drain valve can be lifted during the pause in cycle (about 45 seconds). If the drain valve can be moved, the machine will need to be serviced.',
              'Corra un ciclo. Verifique que la válvula de drenaje exterior puede levantarse durante el ciclo de pausa (45 segundos). Si la válvula del drenaje puede moverse, la máquina requiere servicio',
              'Lancez un cycle. Vérifiez si la valve de vidange extérieure peut être soulevée pendant la pause du cycle (environ 45 secondes). Si la valve peut être déplacée, la machine devra être réparée.',
              'Draai een cyclus. Controleer of de externe afvoerklep tijdens de pauze in de cyclus (ongeveer 45 seconden) kan worden opgetild. Als de klep kan bewegen, heeft de machine service nodig.',
              'Eseguire un ciclo. Verificare se la valvola di scarico esterna può essere sollevata durante la pausa del ciclo (circa 45 secondi). Se la valvola si muove, è necessario un intervento di assistenza.',
              'サイクルを1回実行し、サイクルの一時停止中（約45秒）に外付け排水バルブを持ち上げられるか確認してください。動く場合はサービス対応が必要です。',
              'Execute um ciclo. Verifique se a válvula de dreno externa pode ser levantada durante a pausa do ciclo (cerca de 45 segundos). Se a válvula se mover, será necessário serviço na máquina.',
              'Führen Sie einen Zyklus aus. Prüfen Sie, ob das äußere Ablassventil während der Pause (ca. 45 Sekunden) angehoben werden kann. Wenn es sich bewegen lässt, ist ein Service erforderlich.',
            ],
            images: [
              {
                url: 'assets/images/troubleshooting/es-2000/drain-valve.jpg',
                alt: '',
              },
            ],
            optionsLabel: '',
            options: [optionResolved, optionEnd],
          },
        ],
      },
      {
        key: 'es-2000-unfamiliar-noise',
        option: 'troubleshooting.es2000.noise',
        label: [
          'Unfamiliar noise',
          'Ruido extraño',
          'Bruit inconnu',
          'Onbekend geluid',
          'Rumore insolito',
          '聞き慣れない音',
          'Ruído estranho',
          'Ungewohntes Geräusch',
        ],
        steps: [
          {
            key: 'es-2000-noise-water-tank',
            question: 'troubleshooting.es2000.noiseTank',
            label: [
              'Is there water in the tank?',
              '¿Hay agua en el tanque?',
              'Y a-t-il de l’eau dans le réservoir ?',
              'Zit er water in de tank?',
              "C'è acqua nel serbatoio?",
              'タンク内に水はありますか？',
              'Há água no tanque?',
              'Ist Wasser im Tank?',
            ],
            optionsLabel: '',
            options: [
              {
                key: 'es-2000-yes',
                option: 'troubleshooting.yes',
                label: ['Yes', 'Si', 'Oui', 'Ja', 'Sì', 'はい', 'Sim', 'Ja'],
                icon: 'check',
                resolve: false,
                steps: [
                  {
                    key: 'es-2000-noise-intro',
                    showIntro: true,
                    options: [optionStart],
                  },
                  {
                    key: 'es-2000-noise-water-wash-arms',
                    question: 'troubleshooting.es2000.noiseTankYesObjects',
                    label: [
                      'Check for objects stuck in the wash tank that could be obstructing the wash arms or rinse arms.',
                      'Verifique por objetos atascados en el tanque de lavado que puedan estar obstruyendo los brazos de lavado o de rinse',
                      'Vérifiez la présence d’objets bloquant les bras de lavage ou de rinçage dans le réservoir',
                      'Controleer op voorwerpen in de wastank die de was- of spoelarmen kunnen blokkeren',
                      'Verificare la presenza di oggetti nel serbatoio di lavaggio che possano ostruire i bracci di lavaggio o di risciacquo',
                      '洗浄タンク内に、洗浄アームまたはリンスアームの動きを妨げる異物がないか確認してください',
                      'Verifique se há objetos presos no tanque de lavagem que possam obstruir os braços de lavagem ou de enxágue.',
                      'Prüfen Sie, ob sich im Waschtank Gegenstände befinden, die Wasch- oder Spülarme blockieren.',
                    ],
                    optionsLabel: '',
                    options: [optionResolved, optionEnd],
                  },
                ],
              },
              {
                key: 'es-2000-not-sure',
                option: 'troubleshooting.notSure',
                label: [
                  'Not Sure',
                  ' No estoy seguro(a)',
                  'Je ne suis pas sûr(e)',
                  'Niet zeker',
                  'Non sono sicuro(a)',
                  'よくわからない',
                  'Não tenho certeza',
                  'Nicht sicher',
                ],
                icon: 'question',
                resolve: false,
                steps: [],
              },
              {
                key: 'es-2000-no',
                option: 'troubleshooting.no',
                label: [
                  'No',
                  'No',
                  'Non',
                  'Nee',
                  'No',
                  'いいえ',
                  'Não',
                  'Nein',
                ],
                icon: 'cross',
                resolve: false,
                steps: [
                  {
                    key: 'es-2000-noise-no-water-intro',
                    showIntro: true,
                    options: [optionStart],
                  },
                  {
                    key: 'es-2000-noise-no-water-drain',
                    question: 'troubleshooting.es2000.noiseTankNoObjects',
                    label: [
                      'Check for objects blocking the drain. For EC models, ensure the drain is open.',
                      'Verifique por objetos bloqueando el drenaje. Para modelos EC, asegurese que el drenaje esta abierto',
                      'Vérifiez la présence d’objets bloquant le drain. Pour les modèles EC, assurez-vous que le drain est ouvert',
                      'Controleer op voorwerpen die de afvoer blokkeren. Zorg bij EC-modellen dat de afvoer open is',
                      'Verificare eventuali oggetti che bloccano lo scarico. Per i modelli EC, assicurarsi che lo scarico sia aperto',
                      '排水口を塞ぐ異物がないか確認してください。ECモデルでは、排水が開いていることを確認してください。',
                      'Verifique se há objetos bloqueando o dreno. Para modelos EC, certifique-se de que o dreno esteja aberto.',
                      'Prüfen Sie, ob Gegenstände den Abfluss blockieren. Bei EC-Modellen sicherstellen, dass der Abfluss geöffnet ist.',
                    ],
                    optionsLabel: '',
                    options: [optionResolved, optionNext],
                  },
                  {
                    key: 'es-2000-noise-no-water-wash-arms',
                    question: 'troubleshooting.es2000.noiseTankNoArms',
                    label: [
                      'Check for objects stuck in the wash tank that could be obstructing the wash arms or rinse arms.',
                      'Verifique por objetos atascados en el tanque de lavado que puedan estar obstruyendo los brazos de lavado o de rinse',
                      'Vérifiez la présence d’objets bloquant les bras de lavage ou de rinçage dans le réservoir',
                      'Controleer op voorwerpen in de wastank die de was- of spoelarmen kunnen blokkeren',
                      'Verificare la presenza di oggetti nel serbatoio di lavaggio che possano ostruire i bracci di lavaggio o di risciacquo',
                      '洗浄タンク内に、洗浄アームまたはリンスアームの動きを妨げる異物がないか確認してください',
                      'Verifique se há objetos presos no tanque de lavagem que possam obstruir os braços de lavagem ou de enxágue.',
                      'Prüfen Sie, ob sich im Waschtank Gegenstände befinden, die Wasch- oder Spülarme blockieren.',
                    ],
                    optionsLabel: '',
                    options: [optionResolved, optionEnd],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        key: 'es-2000-spray-arm',
        option: 'troubleshooting.es2000.sprayArm',
        label: [
          'Issue with spray arm',
          'Problema con brazos de rocio',
          'Problème avec le bras de pulvérisation',
          'Probleem met sproeiarm',
          'Problema con braccio irroratore',
          'スプレーアームの問題',
          'Problema no braço pulverizador',
          'Problem mit Sprüharm',
        ],
        steps: [
          {
            key: 'es-2000-spray-arm-intro',
            showIntro: true,
            options: [optionStart],
          },
          {
            key: 'es-2000-spray-arm-nozzles',
            question: 'troubleshooting.es2000.sprayArmNozzles',
            label: [
              'Check nozzles for clogs. Remove end caps and rinse.',
              'Verifique por obstrucciones en las boquillas. Remueva las tapas y enjuague',
              'Vérifiez si les buses sont bouchées. Enlevez les bouchons de fin et rincez',
              'Controleer de sproeiers op verstoppingen. Verwijder de eindkappen en spoel',
              'Controllare gli ugelli per eventuali ostruzioni. Rimuovere i tappi terminali e risciacquare',
              'ノズルの詰まりを確認し、エンドキャップを外してすすいでください',
              'Verifique se os bicos estão obstruídos. Remova as tampas de extremidade e enxágue.',
              'Prüfen Sie die Düsen auf Verstopfungen. Endkappen entfernen und ausspülen.',
            ],
            images: [
              {
                url: 'assets/images/troubleshooting/es-2000/spray-a7rm-nozzles.jpg',
                alt: '',
              },
            ],
            optionsLabel: '',
            options: [optionResolved, optionNext],
          },
          {
            key: 'es-2000-spray-arm-end-caps',
            question: 'troubleshooting.es2000.sprayArmEndCaps',
            label: [
              'Check to make sure the end caps are in place. They may have dropped into the scrap screen or not been replaced after cleaning.',
              'Verifique que las tapas de las puntas estan en su puesto. Estas se habrían podido caer en la pantalla de deshechos o no se replazaron despúes de una limpieza',
              'Vérifiez que les bouchons de fin sont en place. Ils ont peut-être tombé dans l’écran de rebut ou n’ont pas été remplacés après le nettoyage',
              'Controleer of de eindkappen op hun plaats zitten. Ze kunnen in het zeefscherm zijn gevallen of na het reinigen niet teruggeplaatst zijn',
              'Verificare che i tappi terminali siano in posizione. Potrebbero essere caduti nella griglia dei residui o non essere stati rimessi dopo la pulizia',
              'エンドキャップが所定の位置にあるか確認してください。清掃後に戻されていない、またはくず受けに落ちている可能性があります。',
              'Verifique se as tampas de extremidade estão no lugar. Elas podem ter caído na tela de resíduos ou não ter sido recolocadas após a limpeza.',
              'Stellen Sie sicher, dass die Endkappen an ihrem Platz sind; sie könnten in das Sieb gefallen sein oder wurden nach der Reinigung nicht eingesetzt.',
            ],
            optionsLabel: '',
            options: [optionResolved, optionEnd],
          },
        ],
      },
      {
        key: 'es-2000-other',
        label: [
          'Other',
          'Otro',
          'Autre',
          'Overig',
          'Altro',
          'その他',
          'Outro',
          'Sonstiges',
        ],
        option: 'troubleshooting.es2000.other',
      },
    ],
  };
}
